/**
 * A fill that lands on both sides of the body.
 *
 * Phase 4 item 2 of the mirror plan — "flood fill that crosses the centreline
 * correctly". Symmetry was a property of the BRUSH, so turning it on and then
 * tapping fill produced half a garment, which is the least obvious way for a
 * mode to be half-implemented: the toggle is lit, the tool is the wrong one.
 *
 * ## Mirror the stamp, not the result
 *
 * The whole operation is built on a scratch and mirrored there, before anything
 * touches the layer. Copying finished texels afterwards is the version that
 * looks equivalent and is not, and this codebase has already paid for it once:
 * a copy REPLACES what is on the far side, so a soft edge punches partial
 * transparency into whatever was underneath — measured at 72 texels, worst case
 * 255 down to 40, and visible as an outline wherever strokes overlapped.
 *
 * A fill is hard-edged, so that particular symptom would not show. Two others
 * would. `source-atop` — the alpha lock, "paint inside only" — has to apply to
 * the mirrored half as well, and a post-hoc copy bypasses the composite
 * operation entirely, writing colour outside the shape the lock exists to
 * protect. And a fill under reduced opacity would land at full strength on the
 * far side. Compositing once, from a stamp that already contains both halves,
 * gets all of that for free because it is the same code path.
 *
 * ## Why a callback rather than a description of the fill
 *
 * Two fill modes draw differently — whole faces are rects, a flood is a mask
 * blitted from a patch — and a parameter object covering both would be a union
 * that every caller has to destructure and this file has to re-implement. The
 * caller draws; this file decides where, mirrors it, and composites it once.
 */

import { CANVAS } from "../../shared/format";
import type { MirrorMap } from "../../shared/mirror-map";
import { mirrorBoundsFor, mirrorStroke, type Pixels, type Rect } from "../../shared/symmetry";

export interface FillMirrorOptions {
  /** The layer being filled. */
  target: CanvasRenderingContext2D;
  /** What the fill covers before mirroring — the union of the faces it touches. */
  rect: Rect;
  /**
   * Draw the fill, in canvas coordinates.
   *
   * Called against a scratch already translated for you, so a caller passes the
   * same coordinates it would have used against the layer. It must not read
   * back from the scratch: only what it draws is treated as covered, which is
   * what tells the mirror where the fill actually went.
   */
  draw: (ctx: CanvasRenderingContext2D) => void;
  /** The layer's composite mode — carries the alpha lock. */
  composite: GlobalCompositeOperation;
  /** The pairing, or null when symmetry is off. */
  mirror: MirrorMap | null;
  /** Whether the mirror may write here. See MirrorWriteOptions in symmetry.ts. */
  allow?: (x: number, y: number) => boolean;
  doc?: Document;
}

/**
 * Where a fill will land once its mirror is included.
 *
 * Separate from `fillWithMirror` because the caller has to read its undo
 * snapshot BEFORE anything is written, and the snapshot has to cover both
 * halves or undo restores one and leaves the other.
 */
export function fillBounds(rect: Rect, mirror: MirrorMap | null): Rect {
  return mirror ? mirrorBoundsFor(rect, mirror) : rect;
}

/**
 * Draw a fill and its mirror onto the layer, as one composite.
 *
 * Returns the rect actually written, which is `fillBounds` — handed back so a
 * caller cannot compute it twice and get two answers.
 */
export function fillWithMirror(opts: FillMirrorOptions): Rect {
  const doc = opts.doc ?? document;
  const bounds = fillBounds(opts.rect, opts.mirror);

  const scratch = doc.createElement("canvas");
  scratch.width = Math.max(1, Math.round(bounds.w));
  scratch.height = Math.max(1, Math.round(bounds.h));
  const g = scratch.getContext("2d", { willReadFrequently: true });
  if (!g) return bounds;

  /*
    Translated, so the caller draws in canvas coordinates.

    The alternative is every caller subtracting the origin from every rect it
    draws, which is the kind of arithmetic that is right until somebody adds a
    third fill mode.
  */
  g.save();
  g.translate(-bounds.x, -bounds.y);
  opts.draw(g);
  g.restore();

  if (opts.mirror) {
    const view = g.getImageData(0, 0, scratch.width, scratch.height);
    /*
      Target and coverage are the same buffer: the stamp is both what gets
      copied and what says where. `mirrorStroke` reads every source before
      writing any, so the aliasing is safe by construction — the same call
      shape paint.ts uses for a stroke.
    */
    const stamp: Pixels = {
      width: scratch.width,
      height: scratch.height,
      data: view.data,
      originX: bounds.x,
      originY: bounds.y,
    };
    if (mirrorStroke(stamp, stamp, opts.rect, opts.mirror, { allow: opts.allow })) {
      g.putImageData(view, 0, 0);
    }
  }

  const ctx = opts.target;
  ctx.save();
  ctx.globalCompositeOperation = opts.composite;
  ctx.drawImage(scratch, bounds.x, bounds.y);
  ctx.restore();
  return bounds;
}

/** Clamp a rect to the template, so a mirrored bound cannot run off the edge. */
export function clampToCanvas(r: Rect): Rect {
  const x = Math.max(0, Math.min(CANVAS.width, Math.floor(r.x)));
  const y = Math.max(0, Math.min(CANVAS.height, Math.floor(r.y)));
  return {
    x,
    y,
    w: Math.max(0, Math.min(CANVAS.width - x, Math.ceil(r.w))),
    h: Math.max(0, Math.min(CANVAS.height - y, Math.ceil(r.h))),
  };
}
